/** Phase 3B-1 — Monte Carlo Validation.
 *
 * Displays distribution outputs from POST /api/performance/monte-carlo. Fully
 * transparent about assumptions — every card shows the input that produced it.
 */
import { useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { Play, Loader2, AlertTriangle, Info } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import api from "@/lib/api";

const STATUS_TONE = {
  unavailable:         "text-muted-foreground border-border/50",
  exploratory:         "text-signal-yellow border-current",
  moderate_confidence: "pnl-up border-current",
  stronger_evidence:   "text-signal-green border-current",
};
const STATUS_LABEL = {
  unavailable:         "Unavailable · < 20 trades",
  exploratory:         "Exploratory · 20-49 trades",
  moderate_confidence: "Moderate · 50-99 trades",
  stronger_evidence:   "Stronger Evidence · 100+ trades",
};

function fmtUsd(v) {
  if (v == null) return "—";
  if (Math.abs(v) >= 1_000_000) return `$${(v/1_000_000).toFixed(2)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v/1_000).toFixed(1)}k`;
  return `$${v.toFixed(2)}`;
}

function Card({ label, value, sub, tone, testId }) {
  return (
    <div className="grid-cell !p-3" data-testid={testId}>
      <div className="label-cap">{label}</div>
      <div className={`mono text-lg mt-1 ${tone || ""}`}>{value}</div>
      {sub && <div className="mono text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

export default function MonteCarloSection({ filterParams }) {
  const [config, setConfig] = useState({
    n_simulations: 10_000,
    starting_capital: 100_000,
    ruin_pct: 30,
    position_fraction: 5,   // percent
    include_fees: true,
    block_size: 1,
  });

  const runMutation = useMutation({
    mutationFn: async () => (await api.post("/performance/monte-carlo", {
      ...filterParams,
      n_simulations: Number(config.n_simulations),
      starting_capital: Number(config.starting_capital),
      ruin_pct: Number(config.ruin_pct),
      position_fraction: Number(config.position_fraction) / 100,
      include_fees: config.include_fees,
      block_size: Number(config.block_size),
      seed: 42, // reproducible for now — remove or make configurable later
    })).data,
  });

  const data = runMutation.data;
  const status = data?.status;
  const outcomes = data?.outcomes;
  const dist = data?.distribution?.ending_equity_histogram || [];

  return (
    <section className="space-y-3" aria-labelledby="perf-mc-title" data-testid="perf-monte-carlo-section">
      <div className="flex items-center gap-2 flex-wrap">
        <h2 id="perf-mc-title" className="label-cap">Monte Carlo Validation</h2>
        {status && (
          <span className={`chip !text-[10px] uppercase ${STATUS_TONE[status] || ""}`} data-testid={`mc-status-${status}`}>{STATUS_LABEL[status] || status}</span>
        )}
        <Popover>
          <PopoverTrigger className="text-muted-foreground/70 hover:text-foreground focus:outline-none focus:ring-1 focus:ring-current" aria-label="What is Monte Carlo">
            <Info size={12}/>
          </PopoverTrigger>
          <PopoverContent className="w-80 text-xs">
            Monte Carlo simulations bootstrap your historical trade returns thousands of times to
            estimate the distribution of possible outcomes. They are not predictions — they show
            what your equity curve <em>could</em> have looked like under different trade orderings,
            given your observed win rate, average winner, and average loser.
          </PopoverContent>
        </Popover>
        <button
          onClick={() => runMutation.mutate()}
          disabled={runMutation.isPending}
          className="btn-outline h-7 !px-2 text-[11px] ml-auto"
          data-testid="mc-run-btn"
        >
          {runMutation.isPending ? <Loader2 size={12} className="animate-spin"/> : <Play size={12}/>}
          {runMutation.isPending ? "Simulating…" : (data ? "Re-run" : "Run Simulation")}
        </button>
      </div>

      {/* Configuration */}
      <div className="grid-cell !p-3 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 text-xs" data-testid="mc-config">
        <label className="block">
          <div className="label-cap mb-1">Simulations</div>
          <input type="number" value={config.n_simulations} onChange={(e) => setConfig({ ...config, n_simulations: e.target.value })}
            className="bg-card border border-border/60 h-7 px-2 mono w-full focus:outline-none focus:ring-1 focus:ring-current" min="100" max="50000" step="500"
            data-testid="mc-config-sims" aria-label="Number of simulations"/>
        </label>
        <label className="block">
          <div className="label-cap mb-1">Starting $</div>
          <input type="number" value={config.starting_capital} onChange={(e) => setConfig({ ...config, starting_capital: e.target.value })}
            className="bg-card border border-border/60 h-7 px-2 mono w-full focus:outline-none focus:ring-1 focus:ring-current"
            data-testid="mc-config-capital" aria-label="Starting capital"/>
        </label>
        <label className="block">
          <div className="label-cap mb-1">Ruin %</div>
          <input type="number" value={config.ruin_pct} onChange={(e) => setConfig({ ...config, ruin_pct: e.target.value })}
            className="bg-card border border-border/60 h-7 px-2 mono w-full focus:outline-none focus:ring-1 focus:ring-current" min="5" max="90"
            data-testid="mc-config-ruin" aria-label="Ruin threshold percent"/>
        </label>
        <label className="block">
          <div className="label-cap mb-1">Position %</div>
          <input type="number" value={config.position_fraction} onChange={(e) => setConfig({ ...config, position_fraction: e.target.value })}
            className="bg-card border border-border/60 h-7 px-2 mono w-full focus:outline-none focus:ring-1 focus:ring-current" min="0.5" max="100" step="0.5"
            data-testid="mc-config-pos-frac" aria-label="Position size percentage of equity"/>
        </label>
        <label className="block">
          <div className="label-cap mb-1">Block size</div>
          <input type="number" value={config.block_size} onChange={(e) => setConfig({ ...config, block_size: e.target.value })}
            className="bg-card border border-border/60 h-7 px-2 mono w-full focus:outline-none focus:ring-1 focus:ring-current" min="1" max="20"
            data-testid="mc-config-block" aria-label="Bootstrap block size"/>
        </label>
        <label className="flex items-center gap-1.5 mt-4 text-[11px] mono">
          <input type="checkbox" checked={config.include_fees} onChange={(e) => setConfig({ ...config, include_fees: e.target.checked })}
            className="cursor-pointer" data-testid="mc-config-fees" aria-label="Include fees"/>
          Include fees
        </label>
      </div>

      {runMutation.isPending && (
        <div className="grid-cell text-xs text-muted-foreground" role="status" aria-live="polite" data-testid="mc-loading">
          <Loader2 size={12} className="inline animate-spin mr-2"/>Simulating {config.n_simulations.toLocaleString()} runs…
        </div>
      )}

      {runMutation.isError && (
        <div className="grid-cell border-signal-red/40 bg-signal-red/5 text-xs" role="alert" data-testid="mc-error">
          Monte Carlo failed. {runMutation.error?.response?.data?.detail || "Try again."}
        </div>
      )}

      {status === "unavailable" && (
        <div className="grid-cell border-signal-yellow/40 bg-signal-yellow/5 text-xs flex items-start gap-2" role="status" data-testid="mc-unavailable">
          <AlertTriangle size={14} className="text-signal-yellow shrink-0 mt-0.5"/>
          <div>
            <div className="mono uppercase text-signal-yellow text-[10px]">Unavailable</div>
            <div className="text-muted-foreground mt-0.5">{data?.reason}</div>
          </div>
        </div>
      )}

      {outcomes && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px bg-border/60">
            <Card label="Median Equity" value={fmtUsd(outcomes.median_ending_equity)} sub={`Mean ${fmtUsd(outcomes.mean_ending_equity)}`} tone={outcomes.median_ending_equity > data.assumptions.starting_capital ? "pnl-up" : "pnl-down"} testId="mc-median"/>
            <Card label="Std Dev" value={fmtUsd(outcomes.stdev_ending_equity)} testId="mc-stdev"/>
            <Card label="P5" value={fmtUsd(outcomes.p5)} tone="pnl-down" testId="mc-p5"/>
            <Card label="P25" value={fmtUsd(outcomes.p25)} testId="mc-p25"/>
            <Card label="P75" value={fmtUsd(outcomes.p75)} testId="mc-p75"/>
            <Card label="P95" value={fmtUsd(outcomes.p95)} tone="pnl-up" testId="mc-p95"/>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px bg-border/60">
            <Card label="P(profit)" value={`${outcomes.probability_of_profit_pct}%`} tone={outcomes.probability_of_profit_pct > 60 ? "pnl-up" : outcomes.probability_of_profit_pct < 40 ? "pnl-down" : "text-signal-yellow"} testId="mc-p-profit"/>
            <Card label="P(loss)" value={`${outcomes.probability_of_loss_pct}%`} tone="pnl-down" testId="mc-p-loss"/>
            <Card label="P(ruin)" value={`${outcomes.probability_of_ruin_pct}%`} sub={`< ${data.ruin_definition.type === "equity_floor" ? fmtUsd(data.ruin_definition.threshold) : `${data.ruin_definition.threshold}% loss`}`} tone={outcomes.probability_of_ruin_pct > 5 ? "pnl-down" : ""} testId="mc-p-ruin"/>
            <Card label="Median DD" value={`${outcomes.median_max_drawdown_pct}%`} tone="pnl-down" testId="mc-median-dd"/>
            <Card label="P95 DD" value={`${outcomes.p95_max_drawdown_pct}%`} tone="pnl-down" testId="mc-p95-dd"/>
            <Card label="Longest Losing Streak" value={outcomes.p95_longest_losing_streak} sub={`Median ${outcomes.median_longest_losing_streak}`} testId="mc-losing-streak"/>
          </div>

          {dist.length > 1 && (
            <div className="grid-cell !p-3">
              <div className="label-cap mb-2">Ending Equity Distribution — {data.assumptions.n_simulations.toLocaleString()} simulations</div>
              <ResponsiveContainer width="100%" height={200} minWidth={0}>
                <BarChart data={dist} margin={{ top: 6, right: 12, left: 0, bottom: 4 }}>
                  <XAxis dataKey="lo" fontSize={10} stroke="#64748b" tickFormatter={(v) => fmtUsd(v)}/>
                  <YAxis fontSize={10} stroke="#64748b" width={40}/>
                  <Tooltip formatter={(v, name, p) => [`${v} runs`, `${fmtUsd(p.payload.lo)} – ${fmtUsd(p.payload.hi)}`]}/>
                  <ReferenceLine x={data.assumptions.starting_capital} stroke="#EAB308" strokeDasharray="3 3" label={{ value: "Start", position: "top", fill: "#EAB308", fontSize: 10 }}/>
                  <ReferenceLine x={outcomes.p5} stroke="rgba(255,59,48,0.5)" strokeDasharray="2 2"/>
                  <ReferenceLine x={outcomes.p95} stroke="rgba(52,199,89,0.5)" strokeDasharray="2 2"/>
                  <Bar dataKey="count" fill="#0A84FF" isAnimationActive={false}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="grid-cell !p-3 text-[10px] mono text-muted-foreground space-y-1" data-testid="mc-assumptions">
            <div className="label-cap !mb-0">Assumptions</div>
            <div>• {data.assumptions.n_completed_trades} historical trades · {data.assumptions.n_simulations.toLocaleString()} simulations · bootstrap {data.assumptions.bootstrap_method}{data.assumptions.block_size > 1 ? ` (block ${data.assumptions.block_size})` : ""}</div>
            <div>• Starting capital {fmtUsd(data.assumptions.starting_capital)} · position size {(data.assumptions.position_fraction * 100).toFixed(1)}% of equity per trade</div>
            <div>• Ruin defined as equity falling below <span className="pnl-down">{fmtUsd(data.ruin_definition.equity_at_ruin)}</span> ({data.ruin_definition.type === "equity_floor" ? "absolute floor" : `${data.ruin_definition.threshold}% capital loss`})</div>
            <div className="text-[10px] mt-1 italic">{data.assumptions.note}</div>
          </div>
        </>
      )}
    </section>
  );
}
